import { useState } from 'react';
import type { OutputCategory } from '../../types/nodeConfig';

interface Props {
  node: Record<string, unknown>;
  productTypeOptions: Array<{ id: string; name: string }>;
  readOnly?: boolean;
  onChange: (patch: Record<string, unknown>) => void;
}

const CATEGORIES: { value: OutputCategory; label: string }[] = [
  { value: 'completed', label: 'Completed production' },
  { value: 'waste', label: 'Waste' },
  { value: 'rework', label: 'Rework' },
  { value: 'overflow', label: 'Overflow' },
  { value: 'temporary_exit', label: 'Temporary exit' },
];

export default function OutputNodeConfig({
  node,
  productTypeOptions,
  readOnly,
  onChange,
}: Props) {
  const expected =
    (node.expected_product_type_ids as string[] | undefined) ?? [];
  const [customId, setCustomId] = useState('');

  const toggleProduct = (id: string) => {
    const next = expected.includes(id)
      ? expected.filter((x) => x !== id)
      : [...expected, id];
    onChange({ expected_product_type_ids: next });
  };

  const addCustomId = () => {
    const id = customId.trim();
    if (!id || expected.includes(id)) return;
    onChange({ expected_product_type_ids: [...expected, id] });
    setCustomId('');
  };

  const knownIds = new Set(productTypeOptions.map((p) => p.id));
  const customIds = expected.filter((id) => !knownIds.has(id));

  return (
    <section className="space-y-4 rounded-lg border border-border bg-surface/40 p-4">
      <h3 className="panel-section-title">Output configuration</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-text-muted">Category</label>
          <select
            className="input-field w-full"
            disabled={readOnly}
            value={String(node.category ?? 'completed')}
            onChange={(e) => onChange({ category: e.target.value })}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">
            Target throughput (/min, optional)
          </label>
          <input
            type="number"
            min={0}
            step={0.1}
            className="input-field w-full"
            disabled={readOnly}
            value={
              node.target_throughput_per_minute != null
                ? Number(node.target_throughput_per_minute)
                : ''
            }
            onChange={(e) =>
              onChange({
                target_throughput_per_minute: e.target.value
                  ? Number(e.target.value)
                  : undefined,
              })
            }
          />
          <p className="mt-1 text-[10px] text-text-subtle">
            For KPI tracking only. Leave empty if no throughput target is needed.
          </p>
        </div>
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-text-muted">
          Expected product types (optional)
        </p>
        <p className="mb-2 text-[10px] text-text-subtle">
          For KPI tracking only. Actual products are determined by upstream routing
          and gates. Leave empty to accept any product.
        </p>
        {productTypeOptions.length === 0 ? (
          <p className="text-xs text-text-subtle">
            No product types from project inputs — add custom IDs below.
          </p>
        ) : (
          <ul className="space-y-1">
            {productTypeOptions.map((p) => (
              <li key={p.id}>
                <label className="flex items-center gap-2 text-sm text-text-muted">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={expected.includes(p.id)}
                    onChange={() => toggleProduct(p.id)}
                  />
                  {p.name}
                  <span className="text-text-subtle">({p.id})</span>
                </label>
              </li>
            ))}
          </ul>
        )}

        {customIds.length > 0 && (
          <ul className="mt-2 space-y-1">
            {customIds.map((id) => (
              <li
                key={id}
                className="flex items-center justify-between rounded-md bg-surface px-2 py-1 text-sm text-text-muted"
              >
                <span>{id}</span>
                {!readOnly && (
                  <button
                    type="button"
                    className="btn-danger-ghost"
                    onClick={() => toggleProduct(id)}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {!readOnly && (
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              className="input-field flex-1 text-sm"
              placeholder="Custom product type ID"
              value={customId}
              onChange={(e) => setCustomId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomId();
                }
              }}
            />
            <button type="button" className="btn-secondary text-xs" onClick={addCustomId}>
              Add ID
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
